import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { updateProjectRequestSchema, type ProjectView } from "@portal/schemas";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  Label,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Textarea,
} from "@portal/ui";
import { useState } from "react";
import { useForm } from "react-hook-form";
import type { z } from "zod";
import { changeProjectStatus, updateProject } from "../../api/client";
import { useInvalidate } from "../../api/queries";
import { HomeSection } from "../../components/HomeSection";
import { InlineError } from "../../components/InlineError";
import { ProjectStatusBadge } from "./ProjectStatusBadge";
import { Archive, CircleCheck, MoreHorizontal, Pencil, Play, Save } from "lucide-react";

const formSchema = updateProjectRequestSchema.omit({ revision: true });
type FormValues = z.infer<typeof formSchema>;

/**
 * 概要カード。編集・状態変更は「…」メニューにまとめる(フィードバック 2026-09-07:
 * 日常的に押す操作ではないので前面に出さない)。
 */
export function ProjectOverviewCard({ project }: { project: ProjectView }) {
  const invalidate = useInvalidate();
  const [editing, setEditing] = useState(false);
  const status = useMutation({
    mutationFn: (next: ProjectView["status"]) =>
      changeProjectStatus(project.id, { revision: project.revision, status: next }),
    onSuccess: () => invalidate.projects(),
  });

  return (
    <HomeSection
      data-testid="project-overview-card"
      title="概要"
      action={
        project.canEdit && (
          <>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="概要のメニュー"
                  disabled={status.isPending}
                >
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => setEditing(true)}>
                  <Pencil />
                  編集
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {project.status !== "ACTIVE" && (
                  <DropdownMenuItem onSelect={() => status.mutate("ACTIVE")}>
                    <Play />
                    進行中に戻す
                  </DropdownMenuItem>
                )}
                {project.status === "ACTIVE" && (
                  <DropdownMenuItem onSelect={() => status.mutate("ENDED")}>
                    <CircleCheck />
                    終了にする
                  </DropdownMenuItem>
                )}
                {project.status !== "ARCHIVED" && (
                  <DropdownMenuItem
                    variant="destructive"
                    onSelect={() => status.mutate("ARCHIVED")}
                  >
                    <Archive />
                    アーカイブ
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <EditDialog project={project} open={editing} onOpenChange={setEditing} />
          </>
        )
      }
    >
      <div className="flex flex-col gap-3 text-sm">
        <dl className="grid grid-cols-[6rem_1fr] gap-y-2">
          <dt className="text-muted-foreground">状態</dt>
          <dd>
            <ProjectStatusBadge status={project.status} />
          </dd>
          <dt className="text-muted-foreground">コード</dt>
          <dd>{project.code ?? "—"}</dd>
          <dt className="text-muted-foreground">概要</dt>
          <dd className="whitespace-pre-wrap">{project.description || "—"}</dd>
          <dt className="text-muted-foreground">更新</dt>
          <dd>{new Date(project.updatedAt).toLocaleString("ja-JP")}</dd>
        </dl>
        <InlineError error={status.error} />
      </div>
    </HomeSection>
  );
}

function EditDialog({
  project,
  open,
  onOpenChange,
}: {
  project: ProjectView;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const invalidate = useInvalidate();
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    values: { name: project.name, code: project.code ?? "", description: project.description },
  });
  const save = useMutation({
    mutationFn: (v: FormValues) => updateProject(project.id, { ...v, revision: project.revision }),
    onSuccess: async () => {
      await invalidate.projects();
      onOpenChange(false);
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          onSubmit={form.handleSubmit((v) => save.mutate(v))}
          noValidate
          className="flex h-full flex-col gap-4"
        >
          <DialogHeader>
            <DialogTitle>概要を編集</DialogTitle>
            <DialogDescription>{project.name}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4 px-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="edit-name">プロジェクト名</Label>
              <Input id="edit-name" {...form.register("name")} />
              {form.formState.errors.name && (
                <p className="text-xs text-destructive">{form.formState.errors.name.message}</p>
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="edit-code">プロジェクトコード</Label>
              <Input id="edit-code" {...form.register("code")} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="edit-description">概要</Label>
              <Textarea id="edit-description" rows={6} {...form.register("description")} />
            </div>
            <InlineError error={save.error} />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={save.isPending}>
              <Save />
              保存
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
